/**
 * Admin review queue (v1-2-5) — route /admin.
 *
 * Two sections: pending Applications (public intake awaiting triage) and the
 * campaign review queue (submitted campaigns awaiting the operator workflow).
 * Each row links to its detail screen. Every panel handles loading/empty/error
 * independently; a 401 is handled globally by authedFetch (redirect to login).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminShell } from '../components/AdminShell';
import { ApiError, type CampaignRead } from '../lib/api';
import {
  listApplications,
  listCampaignQueue,
  type ApplicationListItem,
} from '../lib/adminApi';

type Load<T> =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; data: T };

function useLoad<T>(loader: () => Promise<T>): Load<T> {
  const [state, setState] = useState<Load<T>>({ phase: 'loading' });
  useEffect(() => {
    let live = true;
    loader()
      .then((data) => live && setState({ phase: 'ready', data }))
      .catch((err) => {
        if (!live) return;
        // UnauthorizedError already triggered a redirect — leave the spinner.
        if (err instanceof ApiError && err.status === 401) return;
        setState({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'Something went wrong.',
        });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
      <h2 className="mb-4 text-base font-semibold text-ink">{title}</h2>
      {children}
    </section>
  );
}

function StatusPill({ value }: { value: string }) {
  return (
    <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-ink-700">
      {value}
    </span>
  );
}

function ApplicationsPanel() {
  const state = useLoad<ApplicationListItem[]>(() => listApplications());
  return (
    <Panel title="Applications">
      {state.phase === 'loading' && <p className="text-sm text-ink-500">Loading…</p>}
      {state.phase === 'error' && <p className="text-sm text-red-600">{state.message}</p>}
      {state.phase === 'ready' && state.data.length === 0 && (
        <p className="text-sm text-ink-500">No applications yet.</p>
      )}
      {state.phase === 'ready' && state.data.length > 0 && (
        <ul className="divide-y divide-line">
          {state.data.map((a) => (
            <li key={a.id}>
              <Link
                to={`/admin/applications/${a.id}`}
                className="flex items-center justify-between py-3 hover:bg-surface-muted"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-ink">
                    {a.company_name}
                  </span>
                  <span className="block truncate text-xs text-ink-500">
                    {a.prize_product} · {a.reference}
                  </span>
                </span>
                <StatusPill value={a.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function CampaignsPanel() {
  const state = useLoad<CampaignRead[]>(() => listCampaignQueue());
  return (
    <Panel title="Campaign review queue">
      <Link
        to="/admin/campaigns"
        className="mb-3 inline-block text-sm font-medium text-primary hover:underline"
      >
        View all campaigns (incl. live) →
      </Link>
      {state.phase === 'loading' && <p className="text-sm text-ink-500">Loading…</p>}
      {state.phase === 'error' && <p className="text-sm text-red-600">{state.message}</p>}
      {state.phase === 'ready' && state.data.length === 0 && (
        <p className="text-sm text-ink-500">No campaigns awaiting review.</p>
      )}
      {state.phase === 'ready' && state.data.length > 0 && (
        <ul className="divide-y divide-line">
          {state.data.map((c) => (
            <li key={c.id}>
              <Link
                to={`/admin/campaigns/${c.id}`}
                className="flex items-center justify-between py-3 hover:bg-surface-muted"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-ink">{c.name}</span>
                  <span className="block truncate text-xs text-ink-500">
                    {c.prize_title ?? 'No prize set'}
                  </span>
                </span>
                <StatusPill value={c.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Navigation card to the Deals & Proposals workspace (/admin/deals). Unlike the
 * data panels above, this is a pure call-to-action — the workspace is too rich
 * to summarise inline, so the card surfaces it for discoverability on every
 * viewport (the AdminShell top-nav link is desktop-only).
 */
function DealsCard() {
  return (
    <Link
      to="/admin/deals"
      className="group flex cursor-pointer flex-col rounded-2xl border border-line bg-white p-6 shadow-card transition duration-200 hover:border-primary/40 hover:shadow-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 7a2 2 0 0 1 2-2h3l2 2h9a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
            <path d="M12 11v4M10 13h4" />
          </svg>
        </span>
        <h2 className="text-base font-semibold text-ink">Deals &amp; Proposals</h2>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-ink-700">
        Creative Library, pitch generators (PDF · video · image · voiceover), and
        client proposal links.
      </p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
        Open workspace
        <svg
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </span>
    </Link>
  );
}

export function AdminQueuePage() {
  return (
    <AdminShell title="Review queue">
      <div className="grid gap-6 lg:grid-cols-2">
        <ApplicationsPanel />
        <CampaignsPanel />
        <DealsCard />
      </div>
    </AdminShell>
  );
}

export default AdminQueuePage;
